import { airlineName } from "../airline-names";
import { escapeHtml } from "../escape";
import { findFlight } from "../find-flight";
import { $flights, $planes, $selectedId, $signal, $theme, selectPlane } from "../store";
import type { FlightRecord, LivePlane } from "../types";

export function renderMobile(host: HTMLElement) {
  host.innerHTML = `
    <div class="mobile-app">
      <div class="m-header"></div>

      <div class="m-top">
        <div class="m-brand"><i class="ph-fill ph-airplane-tilt"></i>Octa Feeder</div>
        <div class="m-pill"><span class="dot"></span><span id="mOnline">ONLINE</span></div>
        <div class="m-pill"><i class="ph ph-cell-signal-full"></i><span id="mDb">47.4dB</span></div>
        <div class="m-pill btn" id="mThemeBtn"><i class="ph ph-moon" id="mThemeIcon"></i></div>
      </div>

      <div class="m-fab">
        <button class="btn" title="Zoom in"><i class="ph ph-plus"></i></button>
        <button class="btn" title="Zoom out"><i class="ph ph-minus"></i></button>
        <button class="btn" title="Locate"><i class="ph ph-crosshair"></i></button>
      </div>

      <div class="m-sheet" id="mSheet">
        <div class="handle" id="mHandle"><span></span></div>
        <div class="m-tabs">
          <button class="m-tab on" data-tab="flights"><i class="ph-fill ph-airplane"></i>Flights<span class="badge" id="mCount" hidden></span></button>
          <button class="m-tab" data-tab="airport"><i class="ph ph-tower-control"></i>Airport</button>
          <button class="m-tab" data-tab="settings"><i class="ph ph-gear"></i>Settings</button>
        </div>
        <div class="m-tabcontent" id="mTabFlights">
          <!-- When a plane is selected, the Web detail card mounts here
               (renderDetail(host) called from main.ts). It shows the same
               rich info as the desktop left rail: route, status badge,
               dep/arr times+gates, aircraft, airline, registration,
               operator, codeshare, etc. The list of all planes follows. -->
          <div id="mDetailHost" class="m-detail-host"></div>
          <div id="mFlightList"></div>
        </div>
        <div class="m-tabcontent" id="mTabAirport" style="display:none">
          <!-- Same renderAirportPanel that powers the Web right rail —
               weather + runways + wind cards stack vertically. -->
          <div id="mAirportPanelHost" class="m-airport-host"></div>
        </div>
        <div class="m-tabcontent" id="mTabSettings" style="display:none">
          <div style="padding:16px;color:var(--muted);font-size:var(--fs-xs);line-height:1.6">
            <h3 style="font-size:var(--fs-2xs);color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin:0 0 10px">Theme</h3>
            <div style="display:flex;gap:6px">
              <button class="m-pill" id="mThemeDark" style="height:34px;padding:0 12px"><i class="ph ph-moon"></i>Dark</button>
              <button class="m-pill" id="mThemeLight" style="height:34px;padding:0 12px"><i class="ph ph-sun"></i>Light</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Theme
  const mThemeBtn = host.querySelector("#mThemeBtn")!;
  const mThemeIcon = host.querySelector("#mThemeIcon") as HTMLElement;
  function syncIcon() {
    mThemeIcon.className = $theme.get() === "dark" ? "ph ph-moon" : "ph ph-sun";
  }
  mThemeBtn.addEventListener("click", () => {
    $theme.set($theme.get() === "dark" ? "light" : "dark");
    syncIcon();
  });
  (host.querySelector("#mThemeDark") as HTMLButtonElement).addEventListener("click", () => {
    $theme.set("dark");
    syncIcon();
  });
  (host.querySelector("#mThemeLight") as HTMLButtonElement).addEventListener("click", () => {
    $theme.set("light");
    syncIcon();
  });
  syncIcon();

  // Signal
  const onlineEl = host.querySelector("#mOnline") as HTMLElement;
  const dbEl = host.querySelector("#mDb") as HTMLElement;
  const countEl = host.querySelector("#mCount") as HTMLElement;
  $signal.subscribe((s) => {
    onlineEl.textContent = s.online ? "ONLINE" : "OFFLINE";
    dbEl.textContent = `${s.dB.toFixed(1)}dB`;
    // Only show the badge once we have a real, non-zero count. The
    // store's default trackedCount is 0 and nanostores fires the
    // subscriber immediately on subscribe, so without this guard the
    // user sees a brief "Flights 0" flash before the first
    // /pifeeder/info poll lands and replaces it with the live number.
    if (s.trackedCount > 0) {
      countEl.textContent = String(s.trackedCount);
      countEl.hidden = false;
    } else {
      countEl.hidden = true;
    }
  });

  // When a plane is selected, expand the bottom sheet so the full detail
  // card (mounted at #mDetailHost by main.ts → renderDetail) is visible.
  $selectedId.subscribe(() => {
    if ($selectedId.get()) host.querySelector("#mSheet")?.classList.add("expanded");
  });

  // Flights list — renders into #mFlightList (the detail card lives in
  // #mDetailHost above it, mounted by main.ts via the shared renderDetail).
  const listEl = host.querySelector("#mFlightList") as HTMLElement;
  function row(p: LivePlane, flights: FlightRecord[]) {
    const active = $selectedId.get() === p.id;
    const flight = findFlight(p.call, flights);
    const route = flight
      ? `<span class="route-pill">${escapeHtml(flight.dep_iata ?? "—")}→${escapeHtml(flight.arr_iata ?? "—")}</span>`
      : "";
    const operator = airlineName(flight?.airline_icao) ?? flight?.airline_icao ?? p.operator ?? p.ctry ?? "";
    // p.id/call/type/operator and the route IATA codes are upstream-controlled
    // (ADS-B feed + airlabs lookup) — escape for attribute + body contexts.
    return `
      <div class="m-flightrow ${active ? "active" : ""}" data-id="${escapeHtml(p.id)}">
        <div class="ic"><i class="ph-fill ph-airplane"></i></div>
        <div class="meta">
          <div class="call">${escapeHtml(p.call)}${route}</div>
          <div class="sub">${escapeHtml(operator)}${p.type ? " · " + escapeHtml(p.type) : ""}</div>
        </div>
        <div class="stat"><span class="k">ALT</span>${p.alt.toLocaleString()}</div>
        <div class="stat"><span class="k">SPD</span>${Math.round(p.spd)} kt</div>
      </div>`;
  }
  function renderList() {
    const planes = Object.values($planes.get()).sort((a, b) => a.call.localeCompare(b.call));
    const flights = $flights.get();
    listEl.innerHTML = planes.length
      ? planes.map((p) => row(p, flights)).join("")
      : `<div style="padding:24px;text-align:center;color:var(--muted)">No aircraft tracked.</div>`;
  }
  listEl.addEventListener("click", (e) => {
    const r = (e.target as HTMLElement).closest("[data-id]") as HTMLElement | null;
    if (r) selectPlane(r.dataset.id ?? null);
  });
  $planes.subscribe(renderList);
  $selectedId.subscribe(renderList);
  $flights.subscribe(renderList);

  // The Airport tab content is mounted by main.ts via renderAirportPanel
  // into #mAirportPanelHost, so we get the full Web right-rail experience
  // (Wx + Runways with mini-map + Wind compass) inside the bottom sheet.

  // Tab switching
  const tabs = host.querySelectorAll<HTMLElement>(".m-tab");
  const panels: Record<string, HTMLElement> = {
    flights: host.querySelector("#mTabFlights")!,
    airport: host.querySelector("#mTabAirport")!,
    settings: host.querySelector("#mTabSettings")!,
  };
  function activateTab(name: string) {
    for (const x of tabs) x.classList.toggle("on", x.dataset.tab === name);
    for (const [k, el] of Object.entries(panels)) el.style.display = k === name ? "" : "none";
  }
  for (const t of tabs) {
    t.addEventListener("click", () => activateTab(t.dataset.tab ?? "flights"));
  }
  // Tapping an airport pin on the map should pop the user into the
  // Airport tab so they actually see the weather/runway data we just
  // loaded. The map.ts click handler dispatches this every tap (even
  // re-taps of the current airport — a $currentIcao subscription would
  // miss those because nanostores skip no-op sets).
  document.addEventListener("pifeeder:airport-tap", () => {
    activateTab("airport");
    sheet.classList.add("expanded");
  });

  // Sheet handle: tap toggles expanded; drag also works
  const sheet = host.querySelector("#mSheet") as HTMLElement;
  const handle = host.querySelector("#mHandle") as HTMLElement;
  handle.addEventListener("click", () => sheet.classList.toggle("expanded"));
  let startY: number | null = null;
  let startH: number | null = null;
  handle.addEventListener(
    "touchstart",
    (e) => {
      startY = e.touches[0]!.clientY;
      startH = sheet.getBoundingClientRect().height;
      sheet.style.transition = "none";
    },
    { passive: true },
  );
  handle.addEventListener(
    "touchmove",
    (e) => {
      if (startY === null || startH === null) return;
      const dy = startY - e.touches[0]!.clientY;
      const newH = Math.min(window.innerHeight * 0.85, Math.max(150, startH + dy));
      sheet.style.height = newH + "px";
    },
    { passive: true },
  );
  handle.addEventListener("touchend", () => {
    startY = null;
    startH = null;
    sheet.style.transition = "";
    const h = sheet.getBoundingClientRect().height;
    sheet.classList.toggle("expanded", h > window.innerHeight * 0.45);
    sheet.style.height = "";
  });
}
